import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Typography,
  Select,
  Button as AntButton,
  Tooltip,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useTheme } from "../../contexts/ThemeContext";
import { useProjects } from "../../hooks/useProjects";
import Card from "../card/Card";
import SettingsPageHeader from "../layout/SettingsPageHeader";
import { Button, Toast } from "../ui";
import Loading from "../Loading";
import SynchronizationService from "../../services/synchronizationService";
import SynchronizationApiConfig from "./SynchronizationApiConfig";
import SynchronizationSchedule from "./SynchronizationSchedule";
import SynchronizationHistory from "./SynchronizationHistory";
import SynchronizationParameters from "./SynchronizationParameters";
import { ProjectHelpCard } from "../projects";
import SynchronizationParametersConfigRefactored from "./parameterConfig/SynchronizationParametersConfigRefactored";
import OmieAuthService from "../../services/OmieAuthService";
import { formatDate } from "../../helpers/datas";
import Api from "../../services/api";
import SynchronizationFileUpload from "./SynchronizationFileUpload";
import { useAuth } from "../../hooks/auth";
import { ACTION, FEATURE } from "../../helpers/permissions";
import BudgetConfig from "./budget/BudgetConfig";
import TenantService from "../../services/tenantService";
import { CustomModal } from "../modal";
import AccessDenied from "../permissions/AccessDenied";
import {
  canUseIncrementalByErp,
  formatIncrementalCountdown,
  getIncrementalCooldownState,
  getIncrementalErpCode,
  getSynchronizationLastStatus,
  isIncrementalAllowedByStatus,
  isSynchronizationConsolidated,
} from "../../helpers/synchronizations";

const { Text } = Typography;

const SynchronizationSettings = ({
  synchronization,
  onBack,
  onSave,
  isIntegration = false,
  onDelete,
  visible = true,
  showHelpCard = true,
  embedded = false,
  tab,
  menu,
  submenu,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    hasPermission,
    canRunIncremental: canRunIncrementalPermission,
    can,
  } = useAuth();
  const { projects: projectsList, loading: loadingProjects } = useProjects();

  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testLocked, setTestLocked] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    razaoSocial: null,
    nomeFantasia: null,
  });
  const [showParameterConfig, setShowParameterConfig] = useState(false);
  const [selectedParameterType, setSelectedParameterType] = useState(null);
  const [incrementalLoading, setIncrementalLoading] = useState(false);
  const [incrementalConfirmOpen, setIncrementalConfirmOpen] = useState(false);
  const [cooldownNow, setCooldownNow] = useState(Date.now());
  const [formData, setFormData] = useState({
    id: null,
    projectId: null, // Importante: usar null, não string vazia
    name: "",
    erp: null, // Importante: usar null, não string vazia
    type: 1, // Usar número em vez de string
    credentials: {
      nomeIntegracao: "",
      appKey: "",
      appSecret: "",
      login: "",
      senha: "",
      authToken: "",
      agrupadorSyncs: [],
      // Campos OAuth2 para Nibo
      owner: "",
      empresa: [],
      arquivos: null,
    },

    // Novos campos para arquivos de orçamento
    budgetConfig: [], // Array de SyncBudgetDto

    // Campos legados (para compatibilidade)
    fileOrcamento: null,
    pendingFile: null,

    status: 1,
    lastStatus: null,
    ultimaSync: null,
    isConfigured: false,
    useErpDreConfig: false,
    registrationCount: 0,
  });

  // Novo estado para listas de parâmetros
  const [syncParameters, setSyncParameters] = useState({
    dfcConfig: [],
    financialGroupConfig: [],
    kpiConfig: [],
    dreConfig: [],
  });

  // Novo: estado para categoryList
  const [categoryList, setCategoryList] = useState([]);
  const canRunIncremental =
    typeof canRunIncrementalPermission === "function"
      ? canRunIncrementalPermission()
      : hasPermission("RUN_INCREMENTAL");
  const canManageIntegrations = can(FEATURE.INTEGRATIONS, [ACTION.MANAGE]);
  const canCreateIntegrations =
    canManageIntegrations || can(FEATURE.INTEGRATIONS, [ACTION.CREATE]);
  const canEditIntegrations =
    canManageIntegrations || can(FEATURE.INTEGRATIONS, [ACTION.EDIT]);
  const isNewSync =
    Boolean(synchronization?.isNew) ||
    (synchronization?.id == null && synchronization?.Id == null);
  const canMutateSync = isNewSync ? canCreateIntegrations : canEditIntegrations;
  const isPermissionBlocked = visible && !canMutateSync;

  const embeddedParams = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );
  const effectiveTab = embedded
    ? embeddedParams.get("syncTab") || tab
    : tab;
  const effectiveMenu = embedded
    ? embeddedParams.get("syncMenu") || menu
    : menu;
  const effectiveSubmenu = embedded
    ? embeddedParams.get("syncSubmenu") || submenu
    : submenu;

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCooldownNow(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const incrementalContext = useMemo(() => {
    const erpCode = getIncrementalErpCode(formData.erp);
    const erpSupported = canUseIncrementalByErp(formData.erp);
    const isConsolidated = isSynchronizationConsolidated(formData);
    const statusAllowsIncremental = isIncrementalAllowedByStatus(
      getSynchronizationLastStatus(formData),
    );
    const cooldownState = getIncrementalCooldownState({
      lastRequestIncAt: formData.lastRequestIncAt,
      userRequestInc: formData.userRequestInc,
      currentUserEmail: user?.email,
      now: cooldownNow,
    });

    return {
      erpCode,
      erpSupported,
      cooldownState,
      countdownLabel: formatIncrementalCountdown(cooldownState.remainingMs),
      canRequest:
        canRunIncremental &&
        erpSupported &&
        !cooldownState.isBlocked &&
        statusAllowsIncremental &&
        !synchronization?.isNew &&
        !!formData.id,
      isConsolidated,
      statusAllowsIncremental,
      integrationId:
        formData.integrationId ||
        formData.ExternalId ||
        synchronization?.integrationId ||
        synchronization?.externalId ||
        synchronization?.ExternalId ||
        formData.id,
    };
  }, [
    canRunIncremental,
    cooldownNow,
    formData.ExternalId,
    formData.erp,
    formData.id,
    formData.integrationId,
    formData.lastStatus,
    formData.lastRequestIncAt,
    formData.status,
    formData.userRequestInc,
    synchronization?.ExternalId,
    synchronization?.externalId,
    synchronization?.id,
    synchronization?.integrationId,
    synchronization?.isNew,
    user?.email,
  ]);

  useEffect(() => {
    if (synchronization) {
      // Configurações adicionais agora são carregadas dentro dos subcomponentes (EmbeddedConfigManager)
      // para exibir loading individual e garantir dados frescos na navegação.

      // Novo padrão: extrair campos do DTO
      const newFormData = {
        id: synchronization.id || synchronization.Id || null,
        projectId:
          synchronization.projectId || synchronization.ProjectId
            ? String(synchronization.projectId || synchronization.ProjectId)
            : null,
        erp: synchronization.erp ?? synchronization.Erp ?? null,
        name: synchronization.name || synchronization.Name || "",
        lastSync: synchronization.lastSync || synchronization.LastSync || null,
        created: synchronization.created || synchronization.Created,
        updated: synchronization.updated || synchronization.Updated,
        createdBy: synchronization.createdBy || synchronization.CreatedBy,
        updatedBy: synchronization.updatedBy || synchronization.UpdatedBy,
        isConfigured:
          synchronization.isConfigured || synchronization.IsConfigured || false,
        useErpDreConfig:
          synchronization.useErpDreConfig ??
          synchronization.UseErpDreConfig ??
          false,
        ExternalId:
          synchronization.ExternalId || synchronization.externalId || null,
        integrationId:
          synchronization.integrationId ||
          synchronization.IntegrationId ||
          synchronization.ExternalId ||
          synchronization.externalId ||
          synchronization.id ||
          synchronization.Id ||
          null,
        lastRequestIncAt:
          synchronization.lastRequestIncAt ||
          synchronization.LastRequestIncAt ||
          null,
        userRequestInc:
          synchronization.userRequestInc ||
          synchronization.UserRequestInc ||
          null,
        status: synchronization.status,
        lastStatus:
          synchronization.lastStatus ??
          synchronization.LastStatus ??
          synchronization.response?.lastStatus ??
          synchronization.response?.LastStatus ??
          synchronization.Response?.lastStatus ??
          synchronization.Response?.LastStatus ??
          synchronization.status ??
          synchronization.Status ??
          null,
        registrationCount: synchronization.registrationCount,
        syncType: synchronization.syncType || synchronization.SyncType || 1,
        fileOrcamento: synchronization.fileOrcamento,
        // Extrai credenciais para o objeto credentials
        credentials: {
          ...(synchronization.credentials || synchronization.Credentials || {}),
          agrupadorSyncs:
            synchronization.credentials?.agrupadorSyncs ||
            synchronization.credentials?.AgrupadorSyncs ||
            synchronization.Credentials?.AgrupadorSyncs ||
            [],
          // Campos OAuth2 para Nibo
          owner:
            synchronization.credentials?.owner ||
            synchronization.Credentials?.Owner ||
            "",
          empresa:
            synchronization.credentials?.empresa ||
            synchronization.Credentials?.Empresa ||
            [],
          authToken:
            synchronization.credentials?.authToken ||
            synchronization.Credentials?.AuthToken ||
            "",
          arquivos:
            synchronization.credentials?.arquivos ||
            synchronization.credentials?.Arquivos ||
            synchronization.Credentials?.Arquivos ||
            synchronization.arquivos ||
            synchronization.Arquivos ||
            null,
        },
        type: synchronization.type || synchronization.Type || 1,
        // Parâmetros antigos (para compatibilidade)
        dfcConfig: synchronization.dfcConfig || synchronization.SyncDFCs || [],
        financialGroupConfig:
          synchronization.financialGroupConfig ||
          synchronization.SyncFinancialGroups ||
          [],
        kpiConfig: synchronization.kpiConfig || synchronization.SyncKPIs || [],
        dreConfig: synchronization.dreConfig || synchronization.SyncDREs || [],

        // Novos campos para arquivos de orçamento
        budgetConfig: synchronization.budgetConfig || [],
        pendingBudgetFile: null,

        // Campos legados
        pendingFile: null,
      };

      setFormData(newFormData);

      // Preencher parâmetros globais
      setSyncParameters({
        dfcConfig: synchronization.dfcConfig || synchronization.dfcConfig || [],
        financialGroupConfig:
          synchronization.financialGroupConfig ||
          synchronization.financialGroupConfig ||
          [],
        kpiConfig: synchronization.kpiConfig || synchronization.kpiConfig || [],
        dreConfig: synchronization.dreConfig || synchronization.dreConfig || [],
      });
      // LOGS AUTOMÁTICOS
      setTimeout(() => {
        console.log("🟢 [LOAD] formData:", newFormData);
        console.log("🟢 [LOAD] syncParameters:", syncParameters);
      }, 100);
    }
  }, [synchronization]);

  useEffect(() => {
    // Carregar lista de ERPs disponíveis
    loadErps();
  }, []);

  useEffect(() => {
    const handleAutoSelectErp = () => {
      if (formData.erp === null) {
        setFormData((prev) => ({
          ...prev,
          erp: 0,
        }));
      }
    };

    window.addEventListener("tour-auto-select-first-erp", handleAutoSelectErp);

    return () => {
      window.removeEventListener(
        "tour-auto-select-first-erp",
        handleAutoSelectErp,
      );
    };
  }, [formData.erp]);

  const loadErps = async () => {
    try {
      // const availableErps = await SynchronizationService.getAvailableERPs();
      // ERPs já estão definidos no serviço
    } catch (error) {
      console.error("Erro ao carregar ERPs:", error);
    }
  };

  useEffect(() => {
    if (formData.erp === 3) {
      setFormData((prev) => ({
        ...prev,
        type: 1,
      }));
    }
  }, [formData.erp]);

  // useMemo para otimizar recriação das opções de projeto
  const projectOptions = useMemo(() => {
    const options = projectsList.map((project) => ({
      value: String(project.id),
      label: project.name,
    }));
    return options;
  }, [projectsList]);

  const erpOptions = useMemo(() => {
    return [
      { value: 0, label: "Omie" },
      { value: 2, label: "Nibo" },
      { value: 3, label: "Excel" },
      { value: 5, label: "Conta Azul" },
    ];
  }, []);

  // Buscar categoryList ao carregar sincronização existente
  useEffect(() => {
    if (!synchronization.isNew && synchronization.id) {
      SynchronizationService.getSynchronizationCategoryList(synchronization.id).then((res) => {
        setCategoryList(Array.isArray(res) ? res : []);
      });
    } else {
      setCategoryList([]);
    }
  }, [synchronization]);

  // Efeito para sincronizar a aba com a URL
  useEffect(() => {
    if (effectiveTab) {
      // Mapeamento de URL para parameter type
      const typeMap = {
        dfc: "dfc",
        dre: "dre",
        orcamento: "orcamento",
        "grupos-financeiros": "gruposFinanceiros",
        indicadores: "indicadores",
      };

      const mappedType = typeMap[effectiveTab] || effectiveTab;

      setSelectedParameterType(mappedType);
      setShowParameterConfig(true);
    } else {
      setSelectedParameterType(null);
      setShowParameterConfig(false);
    }
  }, [effectiveTab]);

  if (!visible || !synchronization) return null;

  // Helper function para converter strings vazias para null
  const convertEmptyStringToNull = (value) => {
    if (typeof value === "string" && value.trim() === "") {
      return null;
    }
    return value;
  };

  // Helper function para processar credentials
  const processCredentials = (credentials) => {
    if (!credentials) return null;

    const processed = {};
    Object.keys(credentials).forEach((key) => {
      processed[key] = convertEmptyStringToNull(credentials[key]);
    });

    return processed;
  };

  const handleSaveConfig = async (overrides = {}) => {
    try {
      setLoading(true);

      // Validar dados básicos
      if (!formData.name || formData.name.trim() === "") {
        Toast.error("Nome da sincronização é obrigatório");
        return;
      }

      if (!formData.projectId) {
        Toast.error("Projeto é obrigatório");
        return;
      }

      if (formData.erp === null || formData.erp === undefined) {
        Toast.error("ERP é obrigatório");
        return;
      }

      if (formData.erp === 2 && formData.type !== 2) {
        const hasExistingAuth = !!formData.ExternalId;
        const hasNewAuth = !!formData.credentials?.owner && formData.credentials?.empresa?.length > 0;
        if (!hasExistingAuth && !hasNewAuth) {
          Toast.error("Autorize a empresa no Nibo antes de salvar.");
          return;
        }
      }

      // Se não é uma nova sincronização, verificar se ela ainda existe
      if (!synchronization.isNew && formData.id) {
        try {
          const existingSync =
            await SynchronizationService.getSynchronizationById(formData.id);
          if (!existingSync) {
            Toast.error(
              "A sincronização não foi encontrada. Ela pode ter sido excluída.",
            );
            return;
          }
        } catch (error) {
          console.error("❌ Erro ao verificar sincronização existente:", error);
          Toast.error("Erro ao verificar sincronização existente");
          return;
        }
      }

      // Transformar dados para o formato do backend (novo padrão DTO)
      const processedCredentials = processCredentials(formData.credentials);

      const syncDto = {
        Id: formData.id || null,
        ProjectId: formData.projectId,
        Erp: formData.erp,
        Name: formData.name,
        LastSync: formData.lastSync || new Date().toISOString(),
        Credentials: processedCredentials
          ? {
              NomeIntegracao: processedCredentials.nomeIntegracao,
              AppKey: formData.erp === 0 ? processedCredentials.appKey : null,
              AppSecret:
                formData.erp === 0 ? processedCredentials.appSecret : null,
              Login:
                formData.erp === 1 || formData.erp === 2
                  ? processedCredentials.login
                  : null,
              Senha:
                formData.erp === 1 || formData.erp === 2
                  ? processedCredentials.senha
                  : null,
              AgrupadorSyncs:
                formData.type === 2
                  ? processedCredentials.agrupadorSyncs
                  : null,
              // Campos OAuth2 para Nibo
              AccessToken:
                formData.erp === 2 || formData.erp === 4
                  ? processedCredentials.accessToken
                  : null,
              RefreshToken:
                formData.erp === 2 ? processedCredentials.refreshToken : null,
              AuthToken:
                formData.erp === 1 ? processedCredentials.authToken : null,
              Organizations:
                formData.erp === 2 ? processedCredentials.organizations : null,
              Owner: formData.erp === 2 ? processedCredentials.owner : null,
              Empresas:
                formData.erp === 2 ? processedCredentials.empresa : null,
              Arquivos:
                formData.erp === 3 ? processedCredentials.arquivos : null,
            }
          : null,
        // Parâmetros da empresa agora são salvos individualmente (não enviar aqui)
        budgetConfig: formData.budgetConfig,
        ExternalId: overrides.ExternalId ?? formData.ExternalId ?? null,
        fileOrcamento: formData.fileOrcamento || null,
        type: formData.type || 1,
        isConfigured:
          overrides.isConfigured ?? (formData.type === 2 ? true : formData.isConfigured || false),
        UseErpDreConfig: formData.useErpDreConfig || false,
        UseOmieExibeFluxoCategories: false,
        status: formData.status || 1,
        registrationCount: formData.registrationCount || 0,
        Created: formData.created,
        Updated: formData.updated,
        CreatedBy: formData.createdBy,
        UpdatedBy: formData.updatedBy,
      };

      // Log detalhado para debug
      // Logar objeto completo para debug

      // Chamar o serviço de upsert
      const result =
        await SynchronizationService.upsertSynchronization(syncDto);

      if (result) {
        // Se há arquivo pendente e agora temos ID da sincronização, fazer upload
        if (synchronization.isNew && result.id && formData.pendingFile) {
          try {
            const uploadResponse =
              await SynchronizationService.uploadOrcamentoFile(
                result.id,
                formData.pendingFile,
              );

            // Extrair a URL da resposta
            const fileUrl = uploadResponse?.response || uploadResponse;

            // Limpar arquivo pendente e atualizar com o resultado
            setFormData((prev) => ({
              ...prev,
              pendingFile: null,
              fileOrcamento: fileUrl,
            }));

            Toast.success(
              "Sincronização criada e arquivo enviado com sucesso!",
            );
          } catch (uploadError) {
            console.error("Erro no upload do arquivo:", uploadError);
            Toast.warning(
              "Sincronização criada, mas houve erro no upload do arquivo",
            );
          }
        } else {
          Toast.success("Configurações salvas com sucesso!");
        }

        await onSave(result);
      }
    } catch (error) {
      console.error("❌ Erro ao salvar configurações:", error);
      console.error("❌ Detalhes do erro:", error.response?.data);

      let errorMessage = "Erro ao salvar configurações";

      // Tratar erro específico de sincronização não encontrada
      if (
        error.response?.data?.message?.includes(
          "expected to affect 1 row(s), but actually affected 0 row(s)",
        )
      ) {
        errorMessage =
          "A sincronização foi modificada ou excluída por outro usuário. Recarregando dados...";

        // Recarregar dados da sincronização
        try {
          if (formData.id) {
            const updatedSync =
              await SynchronizationService.getSynchronizationById(formData.id);
            if (updatedSync) {
              // Atualizar o estado com os dados mais recentes
              setFormData((prev) => ({
                ...prev,
                ...SynchronizationService.transformDataFromBackend(updatedSync),
              }));
              Toast.warning("Dados atualizados com as últimas modificações");
              return;
            }
          }
        } catch (reloadError) {
          console.error("❌ Erro ao recarregar sincronização:", reloadError);
        }
      } else {
        errorMessage =
          error.response?.data?.message || "Erro ao salvar configurações";
      }

      Toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);

      let credentials = {};
      let callGenericTest = true;

      // Se é uma sincronização existente (não nova), permitir credentials vazios
      const isExistingSync = !synchronization.isNew && formData.id;

      switch (formData.erp) {
        case 0: // Omie
          if (
            !isExistingSync &&
            (!formData.credentials.appKey || !formData.credentials.appSecret)
          ) {
            Toast.error("Preencha APP Key e APP Secret");
            return;
          }
          credentials = {
            nomeIntegracao: formData.credentials.nomeIntegracao,
            appKey: formData.credentials.appKey,
            appSecret: formData.credentials.appSecret,
            erp: formData.erp,
          };
          const request = {
            appkey: formData.credentials.appKey,
            appsecret: formData.credentials.appSecret,
          };
          const result = await OmieAuthService.testConnectionOmie(request);
          setFormData((prev) => ({
            ...prev,
            lastSync: result?.sync?.lastSync || result?.sync || null,
            isConfigured: result?.integrations?.success === true,
          }));
          if (result?.integrations?.success === true) {
            Toast.success("Conexão testada com sucesso!");
            setCompanyInfo({
              razaoSocial: result?.integrations?.razao_social || null,
              nomeFantasia: result?.integrations?.nome_fantasia || null,
            });
          } else {
            Toast.error(
              result?.integrations?.message || "Falha ao testar conexão",
            );
            setCompanyInfo({ razaoSocial: null, nomeFantasia: null });
          }
          if (!synchronization?.isNew) {
            // const payload = {
            //   Id: synchronization.id,
            //   IsConfigured: formData.isConfigured
            // }
            // const response = await OmieAuthService.updateIsConfiguredOmie(payload);
          }
          // Depois de testsr, bloqueia para não tentar novamente até mudar as informações de credenciaos.
          setTestLocked(true);
          callGenericTest = false;
          break;

        case 1: // ContaAzul
        case 2: // Nibo
          if (
            !isExistingSync &&
            (!formData.credentials.login || !formData.credentials.senha)
          ) {
            Toast.error("Preencha Usuário e Chave de Acesso");
            return;
          }
          credentials = {
            nomeIntegracao: formData.credentials.nomeIntegracao,
            login: formData.credentials.login,
            senha: formData.credentials.senha,
            erp: formData.erp,
          };
          break;
        case 3: // Excel
          credentials = {
            nomeIntegracao: formData.credentials.nomeIntegracao,
            arquivos: formData.credentials.arquivos,
            erp: formData.erp,
          };
          break;
        case 4: // Asaas
          credentials = {
            nomeIntegracao: formData.credentials.nomeIntegracao,
            accessToken: formData.credentials.accessToken,
          };
          break;

        default:
          Toast.error("Selecione um ERP válido");
          return;
      }

      if (callGenericTest) {
        await SynchronizationService.testConnection(credentials);
      }
    } catch (error) {
      console.error("❌ Erro ao testar conexão:", error);
      const errorMessage =
        error.response?.data?.message || "Erro ao testar conexão";
      Toast.error(errorMessage);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleConfigureParameter = (parameterType) => {
    // Se não estiver embarcado, atualiza a URL
    if (!embedded && synchronization?.id) {
      const urlMap = {
        dfc: "dfc",
        dre: "dre",
        orcamento: "orcamento",
        gruposFinanceiros: "grupos-financeiros",
        indicadores: "indicadores",
      };
      const urlTab = urlMap[parameterType] || parameterType;
      navigate(
        `/synchronizations/settings/${synchronization.id}/${urlTab}${location.search || ""}`,
      );
    } else {
      setSelectedParameterType(parameterType);
      setShowParameterConfig(true);
      const urlMap = {
        dfc: "dfc",
        dre: "dre",
        orcamento: "orcamento",
        gruposFinanceiros: "grupos-financeiros",
        indicadores: "indicadores",
      };
      const urlTab = urlMap[parameterType] || parameterType;
      const params = new URLSearchParams(location.search || "");
      params.set("tab", params.get("tab") || "synchronizations");
      const idValue = synchronization?.id || formData.id;
      if (idValue) params.set("syncId", String(idValue));
      params.set("syncTab", urlTab);
      if (urlTab === "dre") {
        params.set("syncMenu", effectiveMenu || "structure");
        if (effectiveSubmenu) params.set("syncSubmenu", effectiveSubmenu);
        else params.delete("syncSubmenu");
      } else {
        params.delete("syncMenu");
        params.delete("syncSubmenu");
      }
      navigate({ pathname: location.pathname, search: `?${params.toString()}` });
    }
  };

  const handleBackFromParameterConfig = () => {
    if (!embedded && synchronization?.id) {
      navigate(
        `/synchronizations/settings/${synchronization.id}${location.search || ""}`,
      );
    } else {
      setShowParameterConfig(false);
      setSelectedParameterType(null);
      const params = new URLSearchParams(location.search || "");
      params.delete("syncTab");
      params.delete("syncMenu");
      params.delete("syncSubmenu");
      navigate({ pathname: location.pathname, search: `?${params.toString()}` });
    }
  };

  const handleSaveParameterConfig = (configData) => {
    // Atualiza o estado principal preservando todos os tipos de parâmetros
    const newParams = {
      ...syncParameters,
      // Atualizar apenas os tipos específicos que estão sendo configurados
      ...(configData.dfcConfig !== undefined && {
        dfcConfig: configData.dfcConfig,
      }),
      ...(configData.financialGroupConfig !== undefined && {
        financialGroupConfig: configData.financialGroupConfig,
      }),
      ...(configData.kpiConfig !== undefined && {
        kpiConfig: configData.kpiConfig,
      }),
      ...(configData.dreConfig !== undefined && {
        dreConfig: configData.dreConfig,
      }),
    };

    setSyncParameters(newParams);
    setShowParameterConfig(false);
    setSelectedParameterType(null);
    Toast.success("Configurações do parâmetro salvas com sucesso!");
  };

  const handleScheduleChange = (scheduleData) => {
    setFormData((prev) => {
      // Sempre atualizar, não fazer comparação para evitar problemas com valores vazios
      return {
        ...prev,
        name: scheduleData.name !== undefined ? scheduleData.name : prev.name,
        type: scheduleData.type !== undefined ? scheduleData.type : prev.type,
      };
    });
  };

  const handleApiConfigChange = (apiData) => {
    if (typeof apiData === "function") {
      const newValues = apiData(formData.credentials);
      setFormData((prev) => ({
        ...prev,
        credentials: newValues,
      }));
      // desbloqueia quando altera os dados de credenciais, pode testart de novo!
      setTestLocked(false);
      setCompanyInfo({ razaoSocial: null, nomeFantasia: null });
    } else {
      setFormData((prev) => {
        const updates = {};

        // Se campos top-level estão sendo atualizados, adicionar ao formData principal
        if ("IsConfigured" in apiData) {
          updates.isConfigured = apiData.IsConfigured;
        }
        if ("ExternalId" in apiData) {
          updates.ExternalId = apiData.ExternalId;
        }
        // Atualizar credentials com o resto dos dados (exceto campos top-level)
        const { IsConfigured, ExternalId, ...credentialUpdates } = apiData;

        return {
          ...prev,
          ...updates,
          credentials: {
            ...prev.credentials,
            ...credentialUpdates,
          },
        };
      });

      // Também limpa as informações da empresa quando há alteração real de credenciais.
      if (!("IsConfigured" in apiData)) {
        setTestLocked(false);
        setCompanyInfo({ razaoSocial: null, nomeFantasia: null });
      }
    }
  };

  const handleNiboAuthorized = ({ owner, empresas, companyName }) => {
    setFormData(prev => ({
      ...prev,
      isConfigured: true,
      credentials: {
        ...prev.credentials,
        owner,
        empresa: empresas,
      },
    }));
    Toast.success(`Empresa "${companyName}" autorizada! Clique em Salvar para finalizar.`);
  };

  const handleMigrateToV2 = async () => {
    console.log("Migrar para V2");
    try {
      setLoading(true);
      const response = await SynchronizationService.migrateToCAV2(formData.id);
      Toast.success("Sincronização migrada para V2 com sucesso!");
      setFormData((prev) => ({
        ...prev,
        erp: response.erp,
        isConfigured: response.isConfigured,
      }));
    } catch (error) {
      console.error("Erro ao migrar para V2:", error);
      Toast.error("Erro ao migrar para V2: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestIncremental = async () => {
    try {
      if (!incrementalContext.canRequest) {
        if (!canRunIncremental) {
          Toast.error("Você não tem permissão para executar incremental");
        }
        return;
      }

      if (!incrementalContext.erpCode || !incrementalContext.integrationId) {
        Toast.error(
          "Não foi possível identificar a integração para incremental",
        );
        return;
      }

      setIncrementalLoading(true);
      await TenantService.requestIncrementalLoad(
        incrementalContext.integrationId,
        incrementalContext.erpCode,
      );

      const requestedAt = new Date().toISOString();
      setFormData((prev) => ({
        ...prev,
        lastRequestIncAt: requestedAt,
        userRequestInc: user?.email || prev.userRequestInc,
      }));

      if (formData.id) {
        try {
          const reloaded = await SynchronizationService.getSynchronizationById(
            formData.id,
          );
          if (reloaded) {
            const normalized =
              SynchronizationService.transformDataFromBackend(reloaded);
            setFormData((prev) => ({
              ...prev,
              lastRequestIncAt:
                normalized.lastRequestIncAt || prev.lastRequestIncAt,
              userRequestInc: normalized.userRequestInc || prev.userRequestInc,
              integrationId: normalized.integrationId || prev.integrationId,
            }));
          }
        } catch (reloadError) {
          console.error(
            "Erro ao recarregar sincronização após incremental:",
            reloadError,
          );
        }
      }

      Toast.success("Incremental iniciado");
    } catch (error) {
      if (error?.response?.status === 403) {
        Toast.error("Você não tem permissão para executar incremental");
        return;
      }
      Toast.error(error?.response?.data?.message || "Erro ao solicitar incremental");
    } finally {
      setIncrementalLoading(false);
    }
  };

  const handleOpenIncrementalConfirm = () => {
    if (!incrementalContext.canRequest || incrementalLoading) {
      return;
    }
    setIncrementalConfirmOpen(true);
  };

  // Obter nome do ERP selecionado
  const getSelectedErpName = () => {
    const selectedErp = erpOptions.find((e) => e.value === formData.erp);
    return selectedErp ? selectedErp.label : "ERP";
  };

  const handleBackToSynchronizations = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    const params = new URLSearchParams();
    params.set("tab", "synchronizations");
    if (formData.projectId) {
      params.set("projectId", formData.projectId);
    }
    navigate(`/reports?${params.toString()}`);
  };

  // Callback para atualização em tempo real dos parâmetros
  const handleParametersRealtime = (obj) => {
    setSyncParameters((prev) => {
      const newParams = {
        ...prev,
        // Atualizar apenas os tipos específicos que estão sendo modificados
        ...(obj.dfcConfig !== undefined && { dfcConfig: obj.dfcConfig }),
        ...(obj.financialGroupConfig !== undefined && {
          financialGroupConfig: obj.financialGroupConfig,
        }),
        ...(obj.kpiConfig !== undefined && { kpiConfig: obj.kpiConfig }),
        ...(obj.dreConfig !== undefined && { dreConfig: obj.dreConfig }),
      };

      return newParams;
    });
  };

  const handleDreTabChange = (activeTab, activeIndicatorSubMenu) => {
    if (!synchronization?.id) return;
    const menuMap = {
      Structure: "structure",
      Indicators: "indicators",
    };
    const menuSlug = menuMap[activeTab] || "structure";

    if (!embedded) {
      let url = `/synchronizations/settings/${synchronization.id}/dre/${menuSlug}`;
      if (activeTab === "Indicators" && activeIndicatorSubMenu) {
        url += `/${activeIndicatorSubMenu}`;
      }
      navigate(`${url}${location.search || ""}`);
      return;
    }

    const params = new URLSearchParams(location.search || "");
    params.set("tab", params.get("tab") || "synchronizations");
    params.set("syncId", String(synchronization.id));
    params.set("syncTab", "dre");
    params.set("syncMenu", menuSlug);
    if (menuSlug === "indicators" && activeIndicatorSubMenu) {
      params.set("syncSubmenu", String(activeIndicatorSubMenu));
    } else {
      params.delete("syncSubmenu");
    }
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  if (isPermissionBlocked) {
    return (
      <AccessDenied
        title="Sem acesso"
        subTitle="Você não tem permissão para editar esta sincronização."
        showHome={false}
        showBack={false}
        padding={embedded ? 0 : 24}
        extra={
          <AntButton
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              if (typeof onBack === "function") {
                onBack();
                return;
              }
              navigate(-1);
            }}
          >
            Voltar
          </AntButton>
        }
      />
    );
  }

  // Se estamos mostrando a configuração de parâmetros
  if (showParameterConfig && selectedParameterType) {
    if (selectedParameterType === "orcamento") {
      return (
        <BudgetConfig
          syncId={formData.id}
          syncName={formData.name}
          onBack={handleBackFromParameterConfig}
        />
      );
    } else {
      // Passe todas as configs, não só as do type atual
      return (
        <SynchronizationParametersConfigRefactored
          key={`${selectedParameterType}:${effectiveMenu || ""}:${effectiveSubmenu || ""}`}
          parameterType={selectedParameterType}
          onBack={handleBackFromParameterConfig}
          onSave={handleSaveParameterConfig}
          syncId={formData.id}
          existingConfigs={syncParameters} // Passe o objeto inteiro!
          onParametersChange={handleParametersRealtime}
          categoryList={categoryList}
          syncName={formData.name}
          erp={formData.erp}
          embedded={embedded}
          menu={effectiveMenu}
          submenu={effectiveSubmenu}
          onDreTabChange={handleDreTabChange}
        />
      );
    }
  }

  return (
    <div
      style={embedded ? {} : { minHeight: "100vh" }}
      data-tour-id={synchronization.isNew ? "sync-settings" : undefined}
    >
      <div
        style={{
          width: "100%",
          margin: "0 auto",
          ...(synchronization.isNew
            ? {
                maxWidth: 1040,
                padding: 16,
                borderRadius: 12,
                backgroundColor:
                  theme.backgroundSecondary ||
                  (theme.primary ? `${theme.primary}0A` : theme.background),
                border: `1px solid ${theme.borderColor || theme.border}`,
              }
            : {}),
        }}
      >
        <SettingsPageHeader
          topLeft={
            typeof onBack === "function" ? (
              <AntButton
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToSynchronizations}
                style={{
                  paddingInline: 8,
                  color: theme.textSecondary,
                }}
              >
                Voltar
              </AntButton>
            ) : null
          }
          title={synchronization.isNew ? "Criar Sincronização" : "Configurar Integração"}
          subtitle={
            synchronization.isNew
              ? "Defina os dados e parâmetros necessários para a sincronização"
              : `Configure os parâmetros de sincronização com o ${getSelectedErpName()}`
          }
          actions={
            <>
              <Tooltip
                title={
                  !canRunIncremental
                    ? "Você não tem permissão para executar incremental"
                    : !incrementalContext.statusAllowsIncremental
                      ? "Disponível somente quando o status for Sucesso ou Erro/Falha"
                      : incrementalContext.cooldownState.isBlocked
                        ? `Disponível em ${incrementalContext.countdownLabel}`
                        : "Realizar carga incremental"
                }
              >
                <span>
                  <Button
                    type="default"
                    onClick={handleOpenIncrementalConfirm}
                    loading={incrementalLoading}
                    disabled={!incrementalContext.canRequest || incrementalLoading}
                    style={{
                      height: 40,
                      fontSize: 14,
                      borderRadius: 8,
                    }}
                  >
                    {incrementalContext.cooldownState.isBlocked
                      ? `Realizar Carga Incremental (${incrementalContext.countdownLabel})`
                      : "Realizar Carga Incremental"}
                  </Button>
                </span>
              </Tooltip>

              <Button
                type="primary"
                onClick={handleSaveConfig}
                loading={loading}
                style={{
                  backgroundColor: theme.primary,
                  borderColor: theme.primary,
                  height: 40,
                  fontSize: 14,
                  borderRadius: 8,
                }}
                data-tour-id="sync-save-btn"
              >
                {synchronization.isNew
                  ? "Salvar Sincronização"
                  : "Salvar Configuração"}
              </Button>

              {!synchronization.isNew &&
                formData.erp === 1 &&
                user?.userName === "SuporteD4C" && (
                  <Button
                    type="primary"
                    onClick={handleMigrateToV2}
                    loading={loading}
                    style={{
                      backgroundColor: theme.secondary,
                      borderColor: theme.primary,
                      height: 40,
                      fontSize: 14,
                      borderRadius: 8,
                    }}
                  >
                    Migrar para V2
                  </Button>
                )}
            </>
          }
        />

        <Row gutter={[24, 24]}>
          {/* Coluna Esquerda - Configurações */}
          <Col>
            {embedded && (
              <Card
                title="Status da Conexão"
                style={{ marginBottom: 24, borderRadius: 12 }}
                bodyStyle={{ padding: 12 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "8px 10px",
                    backgroundColor:
                      formData.isConfigured === true ? "#f6ffed" : "#fffbe6",
                    borderRadius: 6,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 14,
                          color:
                            formData.isConfigured === true
                              ? "#52c41a"
                              : "#faad14",
                          fontWeight: "bold",
                        }}
                      >
                        {formData.isConfigured === true ? "✓" : "⚠"}
                      </span>
                    </div>
                    <Text style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>
                      {formData.isConfigured ? "Conectado" : "Não Conectado"}
                    </Text>
                  </div>
                  <Button
                    size="small"
                    type="link"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        __showStatusDetails: !prev.__showStatusDetails,
                      }))
                    }
                    style={{ paddingInline: 0 }}
                  >
                    {formData.__showStatusDetails
                      ? "Ocultar detalhes"
                      : "Detalhes"}
                  </Button>
                </div>
                {formData.__showStatusDetails && (
                  <div style={{ padding: "8px 10px" }}>
                    <Text
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#8c8c8c",
                      }}
                    >
                      {formData.isConfigured && !synchronization.isNew
                        ? `Última sincronização: ${
                            formData.lastSync
                              ? formatDate(formData.lastSync)
                              : "Nunca"
                          }`
                        : synchronization.isNew
                          ? ""
                          : "Configure as credenciais e teste a conexão para continuar."}
                    </Text>
                    {formData.erp === 0 &&
                      (companyInfo.razaoSocial || companyInfo.nomeFantasia) && (
                        <div style={{ marginTop: 6 }}>
                          {companyInfo.razaoSocial && (
                            <Text
                              style={{
                                display: "block",
                                fontSize: 12,
                                color: "#595959",
                              }}
                            >
                              Razão social: {companyInfo.razaoSocial}
                            </Text>
                          )}
                          {companyInfo.nomeFantasia && (
                            <Text
                              style={{
                                display: "block",
                                fontSize: 12,
                                color: "#595959",
                              }}
                            >
                              Nome fantasia: {companyInfo.nomeFantasia}
                            </Text>
                          )}
                        </div>
                      )}
                  </div>
                )}
              </Card>
            )}
            {/* Seleção de Projeto e ERP */}
            <Card
              title={
                <span style={{ fontSize: 16, fontWeight: 600 }}>
                  Projeto e ERP
                </span>
              }
              style={{ marginBottom: 24 }}
            >
              {loadingProjects && projectsList.length === 0 ? (
                <Loading small={true} />
              ) : (
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: 8,
                          fontWeight: 500,
                        }}
                      >
                        Projeto <span style={{ color: "#ff4d4f" }}>*</span>
                      </label>
                      <Select
                        value={formData.projectId}
                        onChange={(value) => {
                          setFormData((prev) => ({
                            ...prev,
                            projectId: value,
                          }));
                        }}
                        placeholder="Selecione o projeto"
                        style={{ width: "100%" }}
                        loading={loadingProjects}
                        options={projectOptions}
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        disabled={
                          synchronization?.isNew &&
                          formData.projectId !== null &&
                          isIntegration
                        }
                        data-tour-id={
                          !visible &&
                          loadingProjects &&
                          projectsList.length === 0
                            ? undefined
                            : "sync-project-select"
                        }
                      />
                      {synchronization?.isNew && !isIntegration ? (
                        <AntButton
                          type="link"
                          style={{ padding: 0, height: "auto", marginTop: 6 }}
                          onClick={() => navigate("/company?tab=projects")}
                        >
                          Criar novo projeto
                        </AntButton>
                      ) : null}
                      {synchronization?.isNew &&
                        formData.projectId &&
                        isIntegration && (
                          <Text
                            style={{
                              fontSize: 12,
                              color: "#8c8c8c",
                              marginTop: 4,
                              display: "block",
                            }}
                          >
                            Projeto selecionado automaticamente
                          </Text>
                        )}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: 8,
                          fontWeight: 500,
                        }}
                      >
                        ERP <span style={{ color: "#ff4d4f" }}>*</span>
                      </label>
                      <Select
                        value={formData.erp}
                        onChange={(value) => {
                          setFormData((prev) => ({
                            ...prev,
                            erp: value,
                          }));
                        }}
                        placeholder="Selecione o ERP"
                        style={{ width: "100%" }}
                        options={erpOptions}
                        disabled={!synchronization.isNew}
                        data-tour-id="sync-erp-select"
                      />
                      {!synchronization.isNew && (
                        <Text
                          style={{
                            fontSize: 12,
                            color: "#8c8c8c",
                            marginTop: 4,
                            display: "block",
                          }}
                        >
                          ERP não pode ser alterado após criação
                        </Text>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
            </Card>

            {/* Configurações de Sincronização */}
            <SynchronizationSchedule
              name={formData.name}
              erp={formData.erp}
              syncType={formData.type}
              onChange={handleScheduleChange}
              isEditing={!synchronization.isNew}
            />

            {/* Credenciais de Autenticação - Mostrar apenas se ERP foi selecionado OU se for tipo 2 (agrupador) */}
            {(formData.erp !== null || formData.type === 2) && (
              <SynchronizationApiConfig
                projectId={formData.projectId}
                erp={formData.erp}
                type={formData.type}
                appKey={formData.credentials.appKey}
                appSecret={formData.credentials.appSecret}
                ExternalId={formData.ExternalId}
                login={formData.credentials.login}
                senha={formData.credentials.senha}
                accessToken={formData.credentials.accessToken}
                nomeIntegracao={formData.credentials.nomeIntegracao}
                agrupadorSyncs={formData.credentials.agrupadorSyncs}
                authToken={formData.credentials.authToken}
                owner={formData.credentials.owner}
                empresa={formData.credentials.empresa}
                arquivos={formData.credentials.arquivos}
                idSync={formData.id}
                onChange={handleApiConfigChange}
                onNiboAuthorized={handleNiboAuthorized}
                onTestConnection={handleTestConnection}
                testingConnection={testingConnection}
                isTestLocked={testLocked}
                IsConfigured={formData.isConfigured}
              />
            )}
            {/* Parâmetros da Empresa */}
            {!synchronization.isNew && synchronization.erp !== 4 && (
              <SynchronizationParameters
                onConfigureParameter={handleConfigureParameter}
              />
            )}
          </Col>

          {/* Coluna Direita - Status e Histórico */}
          {!embedded && (
            <Col xs={24} md={24} lg={8}>
              {/* Status da Conexão */}
              <Card
                title="Status da Conexão"
                style={{ marginBottom: 24, borderRadius: 12 }}
                bodyStyle={{ padding: 12 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "8px 10px",
                    backgroundColor:
                      formData.isConfigured === true ? "#f6ffed" : "#fffbe6",
                    borderRadius: 6,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 14,
                          color:
                            formData.isConfigured === true
                              ? "#52c41a"
                              : "#faad14",
                          fontWeight: "bold",
                        }}
                      >
                        {formData.isConfigured === true ? "✓" : "⚠"}
                      </span>
                    </div>
                    <Text style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>
                      {formData.isConfigured ? "Conectado" : "Não Conectado"}
                    </Text>
                  </div>
                  <Button
                    size="small"
                    type="link"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        __showStatusDetails: !prev.__showStatusDetails,
                      }))
                    }
                    style={{ paddingInline: 0 }}
                  >
                    {formData.__showStatusDetails
                      ? "Ocultar detalhes"
                      : "Detalhes"}
                  </Button>
                </div>
                {formData.__showStatusDetails && (
                  <div style={{ padding: "8px 10px" }}>
                    <Text
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#8c8c8c",
                      }}
                    >
                      {formData.isConfigured && !synchronization.isNew
                        ? `Última sincronização: ${
                            formData.lastSync
                              ? formatDate(formData.lastSync)
                              : "Nunca"
                          }`
                        : synchronization.isNew
                          ? ""
                          : "Configure as credenciais e teste a conexão para continuar."}
                    </Text>
                    {formData.erp === 0 &&
                      (companyInfo.razaoSocial || companyInfo.nomeFantasia) && (
                        <div style={{ marginTop: 6 }}>
                          {companyInfo.razaoSocial && (
                            <Text
                              style={{
                                display: "block",
                                fontSize: 12,
                                color: "#595959",
                              }}
                            >
                              Razão social: {companyInfo.razaoSocial}
                            </Text>
                          )}
                          {companyInfo.nomeFantasia && (
                            <Text
                              style={{
                                display: "block",
                                fontSize: 12,
                                color: "#595959",
                              }}
                            >
                              Nome fantasia: {companyInfo.nomeFantasia}
                            </Text>
                          )}
                        </div>
                      )}
                  </div>
                )}
              </Card>

              {/* Histórico de Sincronizações - Apenas para sincronizações existentes */}
              {!synchronization.isNew && synchronization.isHistoric && (
                <SynchronizationHistory synchronizationId={formData.id} />
              )}
            </Col>
          )}
          {showHelpCard && (
            <Col>
              <ProjectHelpCard />
            </Col>
          )}
        </Row>
        <CustomModal
          title="Confirmar carga incremental"
          content={`Deseja iniciar a carga incremental da sincronização "${formData.name || "selecionada"}"?`}
          open={incrementalConfirmOpen}
          onCancel={() => {
            if (incrementalLoading) return;
            setIncrementalConfirmOpen(false);
          }}
          onConfirm={async () => {
            await handleRequestIncremental();
            setIncrementalConfirmOpen(false);
          }}
          confirmText="Confirmar e iniciar"
          cancelText="Cancelar"
          variant="confirmation"
          loading={incrementalLoading}
          okButtonProps={{
            style: {
              backgroundColor: theme.primary,
              borderColor: theme.primary,
            },
          }}
        />
      </div>
    </div>
  );
};

export default SynchronizationSettings;
